import { useState } from 'react';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { login } from '../api/endpoints';
import { errorMessageRu, isTotpRequired } from '../api/errors';
import { Banner } from '../components/Banner';
import { useSession } from './session-context';

/**
 * Login screen.
 *
 * The TOTP field is not shown up front: the server decides whether a second
 * factor is required for this account, answers `TOTP_REQUIRED`, and the field
 * appears with the credentials preserved. Nothing is persisted anywhere — on
 * success the server sets an httpOnly cookie and the session query refetches.
 */

const LoginFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Укажите адрес электронной почты.')
    .email('Некорректный адрес электронной почты.'),
  password: z.string().min(1, 'Введите пароль.'),
  totpCode: z.string(),
});

type LoginForm = z.infer<typeof LoginFormSchema>;

export function LoginPage(): ReactElement {
  const { refresh, expired } = useSession();
  const [totpRequired, setTotpRequired] = useState(false);
  const [serverErrorRu, setServerErrorRu] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { email: '', password: '', totpCode: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerErrorRu(null);
    try {
      const trimmed = values.totpCode.trim();
      await login({
        email: values.email.trim(),
        password: values.password,
        ...(trimmed === '' ? {} : { totpCode: trimmed }),
      });
      refresh();
    } catch (error) {
      if (isTotpRequired(error)) {
        setTotpRequired(true);
        setServerErrorRu(errorMessageRu(error));
        return;
      }
      setServerErrorRu(errorMessageRu(error));
    }
  });

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <div>
          <h1>Вход в панель управления</h1>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Торговый бот BTC-USDT перпетуал. Доступ только для авторизованных операторов.
          </p>
        </div>

        {expired && (
          <Banner tone="warn" title="Сессия завершена.">
            Время сессии истекло или вход был выполнен из другого места. Войдите снова.
          </Banner>
        )}

        {serverErrorRu !== null && (
          <Banner tone="danger" title="Вход не выполнен.">
            {serverErrorRu}
          </Banner>
        )}

        <div className="field">
          <label className="field__label" htmlFor="login-email">
            Электронная почта
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="username"
            aria-invalid={errors.email !== undefined}
            {...register('email')}
          />
          {errors.email !== undefined && (
            <span className="field__error" role="alert">
              {errors.email.message}
            </span>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="login-password">
            Пароль
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.password !== undefined}
            {...register('password')}
          />
          {errors.password !== undefined && (
            <span className="field__error" role="alert">
              {errors.password.message}
            </span>
          )}
        </div>

        {totpRequired && (
          <div className="field">
            <label className="field__label" htmlFor="login-totp">
              Код двухфакторной аутентификации
            </label>
            <input
              id="login-totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              {...register('totpCode')}
            />
            <span className="field__hint">Шесть цифр из приложения-аутентификатора.</span>
          </div>
        )}

        <button type="submit" className="btn btn--primary btn--block" disabled={isSubmitting}>
          {isSubmitting ? 'Проверка…' : 'Войти'}
        </button>
      </form>
    </main>
  );
}
